import { DOCUMENT } from '@angular/common';
import { Inject, Injectable, Renderer2, RendererFactory2, RendererStyleFlags2 } from '@angular/core';

/**
 * Single owner of the application body zoom.
 *
 * The shell applies a compact `0.8` zoom on ≥1024px viewports and `1` otherwise, and mirrors
 * the value into the `--rt-body-zoom` CSS custom property on `<html>` (default `1` in
 * styles.css `:root`) so viewport-unit rules — `vh`/`vw` are NOT scaled by zoom — can divide
 * by it. Centralising both writes here keeps the zoom and the token from ever drifting.
 *
 * Mirrors the same service in the main RegTrack web app, so the two shells stay visually in
 * step. That app also exposes an override that pins the zoom for features which render wrong
 * under it (its BoldBI embed); nothing here needs one, so it is deliberately left out.
 */
@Injectable({ providedIn: 'root' })
export class BodyZoomService {

  /** Media query that decides whether the compact 0.8 body zoom applies (≥1024px viewports). */
  private readonly zoomMediaQuery: MediaQueryList;

  /** Renderer created via the factory — services have no host renderer to inject directly. */
  private readonly renderer: Renderer2;

  /** Guard so the media-query listener is attached exactly once. */
  private watching = false;

  /**
   * @param rendererFactory - Factory used to obtain a renderer for the body/html style writes.
   * @param document - Injected document; also the platform-safe route to `window.matchMedia`.
   */
  constructor(rendererFactory: RendererFactory2, @Inject(DOCUMENT) private document: Document) {
    this.renderer = rendererFactory.createRenderer(null, null);
    this.zoomMediaQuery = this.document.defaultView!.matchMedia('(min-width: 1024px)');
  }

  /**
   * Applies the zoom for the current viewport and starts re-evaluating it on every crossing
   * of the 1024px media-query band (window resized, snapped, moved to another monitor, or
   * the display resolution/scaling changes). Called once from the app shell's `ngOnInit`;
   * subsequent calls only re-apply the current value.
   * @returns Nothing.
   */
  init(): void {
    this.applyResponsiveZoom();
    if (!this.watching) {
      this.watching = true;
      this.zoomMediaQuery.addEventListener('change', () => this.applyResponsiveZoom());
    }
  }

  /**
   * Applies the viewport-dependent body zoom (`0.8` on ≥1024px viewports, `1` otherwise).
   * @returns Nothing.
   */
  applyResponsiveZoom(): void {
    this.setBodyZoom(this.zoomMediaQuery.matches ? '0.8' : '1');
  }

  /**
   * Applies a body zoom and mirrors it into the `--rt-body-zoom` CSS custom property on
   * `<html>` in one place, so the two can never drift.
   *
   * `RendererStyleFlags2.DashCase` is required for the token: it routes Renderer2 to
   * `style.setProperty`, the only path that writes a CSS custom property (plain `setStyle`
   * would assign `el.style['--rt-body-zoom']`, a silent no-op).
   * @param zoom - Zoom factor as a CSS value, e.g. `'0.8'`.
   * @returns Nothing.
   */
  private setBodyZoom(zoom: string): void {
    this.renderer.setStyle(this.document.body, 'zoom', zoom);
    this.renderer.setStyle(this.document.documentElement, '--rt-body-zoom', zoom, RendererStyleFlags2.DashCase);
  }
}
